import React from 'react';
import { AlertTriangle } from 'lucide-react';

/**
 * ErrorBoundary — catches JS errors in a subtree and shows an inline error
 * panel instead of crashing the entire tab / page to a blank screen.
 *
 * Usage: <ErrorBoundary label="Photography tab"> ... </ErrorBoundary>
 */
export class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null, info: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    // Log to the browser console for operator diagnosis.
    // eslint-disable-next-line no-console
    console.error(`[ErrorBoundary:${this.props.label || 'root'}]`, error, info);
    this.setState({ info });
  }

  reset = () => {
    this.setState({ error: null, info: null });
  };

  render() {
    if (!this.state.error) return this.props.children;
    return (
      <div
        className="rounded-md border border-destructive/50 bg-destructive/5 p-4 text-sm"
        data-testid={`error-boundary-${(this.props.label || 'tab').replace(/\s+/g, '-').toLowerCase()}`}
      >
        <div className="flex items-start gap-2">
          <AlertTriangle className="w-4 h-4 mt-0.5 text-destructive shrink-0" />
          <div className="flex-1 min-w-0">
            <div className="font-semibold text-destructive mb-1">
              Something went wrong rendering the {this.props.label || 'panel'}.
            </div>
            <div className="text-muted-foreground break-words font-mono text-xs">
              {String(this.state.error?.message || this.state.error)}
            </div>
            {this.state.info?.componentStack && (
              <details className="mt-2">
                <summary className="cursor-pointer text-xs text-muted-foreground">
                  Component stack
                </summary>
                <pre className="mt-1 whitespace-pre-wrap text-[10px] text-muted-foreground">
                  {this.state.info.componentStack}
                </pre>
              </details>
            )}
            <button
              type="button"
              onClick={this.reset}
              className="mt-2 text-xs underline text-destructive"
            >
              Try again
            </button>
          </div>
        </div>
      </div>
    );
  }
}

export default ErrorBoundary;
